import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  Add as AddIcon,
  Close as CloseIcon,
  PersonOutline as PersonOutlineIcon,
  TagOutlined as TagOutlinedIcon,
  StickyNote2Outlined as StickyNote2OutlinedIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';

const STATUS = ['open', 'assigned', 'resolved'];

/** Purpose: Normalize legacy conversation states. Why: Detail actions and badges must use current lifecycle terms. */
const normalizeStatus = (status) => {
  const normalized = String(status || 'open').trim().toLowerCase();
  if (normalized === 'pending') return 'assigned';
  if (normalized === 'closed') return 'resolved';
  return normalized;
};

/** Purpose: Choose a readable bot or agent conversation status. Why: Active bot conversations need appropriate wording. */
const statusLabel = (status, isBotConversation) => {
  const normalized = normalizeStatus(status);
  if (normalized === 'resolved') return 'Resolved';
  if (isBotConversation && normalized === 'open') return 'Active';
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

/** Purpose: format optional conversation-event timestamps safely. Why: a malformed legacy timestamp must not crash the entire details panel. */
const formatActivityDate = (value) => {
  const date = value ? new Date(value) : null;
  return date && Number.isFinite(date.getTime()) ? format(date, 'PP p') : 'Time unavailable';
};

/** Purpose: Map conversation state to badge styles. Why: Lifecycle changes should be visually distinguishable. */
const statusClass = (status) =>
  normalizeStatus(status) === 'open'
    ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-200'
    : normalizeStatus(status) === 'assigned'
      ? 'bg-amber-500/10 text-amber-700 dark:text-amber-200'
      : 'bg-slate-200 text-slate-600 dark:bg-white/10 dark:text-slate-300';

/** Purpose: Map agent availability to badge styles. Why: Operators must distinguish online, busy, and offline agents. */
const agentStatusClass = (status) =>
  status === 'busy'
    ? 'bg-amber-500/10 text-amber-700 dark:text-amber-200'
    : status === 'online'
      ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-200'
      : 'bg-slate-200 text-slate-600 dark:bg-white/10 dark:text-slate-300';

/** Purpose: display and edit metadata for the selected conversation. Why: assignment, status, tags, and notes should be managed without leaving the thread. */
export default function InboxDetailsPanel({
  conversation,
  agents = [],
  selectedAgent = null,
  onAssign,
  onStatusChange,
  onAddNote,
  onAddTag,
  onRemoveTag,
  isBotConversation = false,
}) {
  const [noteText, setNoteText] = useState('');
  const [tagText, setTagText] = useState('');
  const [activityEvents, setActivityEvents] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState('');
  const [expandedActivityId, setExpandedActivityId] = useState('');
  const [activityOpen, setActivityOpen] = useState(false);
  const activityRequestRef = useRef(0);

  /** Purpose: Resolve an agent ID from raw or populated assignment data. Why: The assignment control requires a stable selected value. */
  const assignedValue = useMemo(() => conversation?.assignedTo?._id || conversation?.assignedTo || '', [conversation]);

  useEffect(() => {
    activityRequestRef.current += 1;
    setActivityEvents([]);
    setActivityError('');
    setActivityLoading(false);
    setExpandedActivityId('');
    setActivityOpen(false);
  }, [conversation?._id]);

  /** Purpose: Fetch activity with conversation and request identity checks. Why: Late replies must not overwrite another thread's events. */
  const loadActivity = async () => {
    if (!conversation?._id || activityLoading) return;
    const conversationId = conversation._id;
    const requestId = ++activityRequestRef.current;
    setActivityLoading(true);
    setActivityError('');
    try {
      const response = await axios.get(`/api/v1/inbox/conversations/${conversationId}/events`);
      if (activityRequestRef.current === requestId) setActivityEvents(Array.isArray(response.data?.events) ? response.data.events : []);
    } catch (error) {
      if (activityRequestRef.current === requestId) setActivityError(error.response?.data?.message || error.response?.data?.error || 'Could not load conversation activity.');
    } finally {
      if (activityRequestRef.current === requestId) setActivityLoading(false);
    }
  };

  /** Purpose: Toggle activity and load events on first expansion. Why: History should be fetched when the operator requests it. */
  const toggleActivity = () => {
    const nextOpen = !activityOpen;
    setActivityOpen(nextOpen);
    if (nextOpen && activityEvents.length === 0) loadActivity();
  };

  /** Purpose: Submit a nonblank private note and clear the draft. Why: Empty notes should not create audit noise. */
  const addNote = async () => {
    const text = noteText.trim();
    if (!text) return;
    await onAddNote?.(text);
    setNoteText('');
  };

  /** Purpose: Submit a nonblank tag and clear the draft. Why: The draft should remain until submission completes. */
  const addTag = async () => {
    const text = tagText.trim();
    if (!text) return;
    await onAddTag?.(text);
    setTagText('');
  };

  return (
    <aside className="flex h-full min-h-0 w-full flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-white/95 p-3 shadow-sm backdrop-blur-xl dark:border-white/10 dark:bg-slate-950/80">
      <div className="flex items-start justify-between gap-2 border-b border-slate-200/80 pb-2.5 dark:border-white/10">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">Details</div>
          <div
            className="mt-0.5 truncate text-sm font-bold text-slate-900 dark:text-white"
            title={conversation?.displayName || 'Conversation details'}
          >
            {conversation?.displayName || 'Conversation details'}
          </div>
          <div className="mt-0.5 truncate text-[11px] text-slate-500 dark:text-slate-400">
            {conversation?.channel || 'Channel'} · {statusLabel(conversation?.status, isBotConversation)}
          </div>
        </div>
        <span className={['rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider shrink-0', statusClass(conversation?.status)].join(' ')}>
          {statusLabel(conversation?.status, isBotConversation)}
        </span>
      </div>

      <div className="mt-2.5 min-h-0 flex-1 space-y-2.5 overflow-y-auto pr-0.5">
        {conversation?.sla?.status && conversation.sla.status !== 'none' ? (
          <div className="rounded-xl border border-slate-200/80 bg-slate-50/70 p-2.5 dark:border-white/10 dark:bg-white/5">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">SLA · {conversation.sla.policyName}</div>
            <div className={`mt-1.5 text-xs font-semibold capitalize ${conversation.sla.status === 'breached' ? 'text-rose-600' : conversation.sla.status === 'warning' ? 'text-amber-600' : 'text-emerald-600'}`}>{conversation.sla.status}</div>
            {conversation.sla.breachedTargets?.length ? <div className="mt-0.5 text-[11px] text-rose-500">Breached: {conversation.sla.breachedTargets.join(', ').replaceAll('_', ' ')}</div> : null}
            <div className="mt-1 text-[11px] text-slate-500">Resolution due: {conversation.sla.resolutionDueAt ? format(new Date(conversation.sla.resolutionDueAt), 'PP p') : '-'}</div>
          </div>
        ) : null}

        <div className="rounded-xl border border-slate-200/80 bg-slate-50/70 p-2.5 dark:border-white/10 dark:bg-white/5">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">Agent status</div>
          <div className="mt-2 flex items-center justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="truncate text-xs font-semibold text-slate-900 dark:text-white">
                {selectedAgent?.name || 'No agent assigned'}
              </div>
              <div className="mt-0.5 truncate text-[11px] text-slate-500 dark:text-slate-400">
                {selectedAgent ? `${selectedAgent.activeChats || 0}/${selectedAgent.maxLimit || 3} chats` : 'Assign a live agent to hand off.'}
              </div>
            </div>
            {selectedAgent ? (
              <span className={['rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider shrink-0', agentStatusClass(selectedAgent.status)].join(' ')}>
                {selectedAgent.status || 'offline'}
              </span>
            ) : null}
          </div>
        </div>

        <div className="rounded-xl border border-slate-200/80 bg-slate-50/70 p-2.5 dark:border-white/10 dark:bg-white/5">
          <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">
            <PersonOutlineIcon sx={{ fontSize: 14 }} />
            Assign agent
          </div>
          <select
            value={assignedValue}
            onChange={(event) => onAssign?.(event.target.value)}
            className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-900 outline-none transition focus:border-cyan-400 focus:ring-1 focus:ring-cyan-500/20 dark:border-white/10 dark:bg-slate-900/80 dark:text-slate-100"
          >
            <option value="">Assign agent</option>
            {agents.map((agent) => (
              <option
                key={agent._id || agent.userId}
                value={agent.userId || agent._id}
                disabled={!agent.available && String(agent.userId || agent._id) !== String(assignedValue)}
              >
                {agent.name}{agent.available ? ' · Available' : ` · ${agent.status || 'offline'}`}
              </option>
            ))}
          </select>
        </div>

        <div className="rounded-xl border border-slate-200/80 bg-slate-50/70 p-2.5 dark:border-white/10 dark:bg-white/5">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">Status</div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {(isBotConversation ? ['open', 'resolved'] : STATUS).map((status) => {
              const active = normalizeStatus(conversation?.status) === status;
              return (
                <button
                  key={status}
                  type="button"
                  onClick={() => onStatusChange?.(status)}
                  className={[
                    'rounded-full px-2.5 py-1 text-xs font-semibold capitalize transition active:scale-95',
                    active
                      ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-950'
                      : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200/70 dark:border-white/10 dark:bg-slate-900/80 dark:text-slate-300 dark:hover:bg-white/10',
                  ].join(' ')}
                >
                  {statusLabel(status, isBotConversation)}
                </button>
              );
            })}
          </div>
        </div>

        <div className="rounded-xl border border-slate-200/80 bg-slate-50/70 p-2.5 dark:border-white/10 dark:bg-white/5">
          <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">
            <TagOutlinedIcon sx={{ fontSize: 14 }} />
            Tags
          </div>
          <div className="mt-2 flex items-center gap-1.5">
            <input
              value={tagText}
              onChange={(event) => setTagText(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  addTag();
                }
              }}
              placeholder="Add tag"
              className="w-full rounded-xl border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-900 outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-500/20 dark:border-white/10 dark:bg-slate-900/80 dark:text-slate-100"
            />
            <button
              type="button"
              onClick={addTag}
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-white transition hover:bg-slate-800 dark:bg-cyan-500 dark:text-slate-950 cursor-pointer"
            >
              <AddIcon sx={{ fontSize: 16 }} />
            </button>
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {(conversation?.tags || []).length ? conversation.tags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center gap-1.5 rounded-full bg-cyan-500/10 px-2.5 py-0.5 text-xs font-medium text-cyan-700 dark:text-cyan-200"
              >
                {tag}
                <button type="button" onClick={() => onRemoveTag?.(tag)} className="opacity-70 transition hover:opacity-100 cursor-pointer">
                  <CloseIcon sx={{ fontSize: 12 }} />
                </button>
              </span>
            )) : (
              <div className="text-xs text-slate-400 dark:text-slate-500">No tags yet.</div>
            )}
          </div>
        </div>

        <div className="rounded-xl border border-slate-200/80 bg-slate-50/70 p-2.5 dark:border-white/10 dark:bg-white/5">
          <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">
            <StickyNote2OutlinedIcon sx={{ fontSize: 14 }} />
            Notes
          </div>
          <div className="mt-2 space-y-1.5">
            <textarea
              value={noteText}
              onChange={(event) => setNoteText(event.target.value)}
              placeholder="Write a private note..."
              rows={2}
              className="w-full resize-none rounded-xl border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-900 outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-500/20 dark:border-white/10 dark:bg-slate-900/80 dark:text-slate-100"
            />
            <button
              type="button"
              onClick={addNote}
              className="rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold text-white transition hover:bg-slate-800 dark:bg-cyan-500 dark:text-slate-950 cursor-pointer"
            >
              Add note
            </button>
          </div>

          <div className="mt-3 space-y-2">
            {(conversation?.notes || []).length ? conversation.notes.slice().reverse().map((note) => (
              <div key={`${note.createdAt}-${note.text}`} className="rounded-xl border border-slate-200/80 bg-white p-2.5 text-xs shadow-2xs dark:border-white/10 dark:bg-slate-900/80">
                <div className="text-slate-700 dark:text-slate-200">{note.text}</div>
                <div className="mt-1.5 text-[10px] text-slate-400 dark:text-slate-500">
                  {note.createdByName || 'Agent'} · {note.createdAt ? format(new Date(note.createdAt), 'PP p') : ''}
                </div>
              </div>
            )) : (
              <div className="text-xs text-slate-400 dark:text-slate-500">No notes yet.</div>
            )}
          </div>
        </div>

        <section className="rounded-xl border border-slate-200/80 bg-slate-50/70 p-2.5 text-xs dark:border-white/10 dark:bg-white/5">
          <div className="flex items-center justify-between gap-2">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">Activity log</div>
              <div className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">Status, assignment, SLA, and agent events</div>
            </div>
            <button type="button" onClick={toggleActivity} aria-expanded={activityOpen} className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-slate-700 hover:border-cyan-300 dark:border-white/10 dark:bg-slate-900 dark:text-slate-200">
              {activityOpen ? 'Hide' : 'View'}
            </button>
          </div>
          {activityOpen ? (
            <div className="mt-2 space-y-1.5">
              {activityLoading ? <div className="py-2 text-slate-500">Loading activity…</div> : null}
              {activityError ? <div className="rounded-lg bg-rose-500/10 p-2 text-rose-700 dark:text-rose-200">{activityError}<button type="button" onClick={loadActivity} className="ml-2 font-semibold underline">Retry</button></div> : null}
              {!activityLoading && !activityError && activityEvents.length === 0 ? <div className="py-2 text-slate-500">No recorded activity yet.</div> : null}
              {activityEvents.slice(0, 50).map((entry, index) => {
                const eventId = String(entry._id || `${entry.type}-${entry.createdAt}-${index}`);
                const isExpanded = expandedActivityId === eventId;
                return (
                  <div key={eventId} className="overflow-hidden rounded-lg border border-slate-200 bg-white dark:border-white/10 dark:bg-slate-900/80">
                    <button type="button" onClick={() => setExpandedActivityId((current) => current === eventId ? '' : eventId)} aria-expanded={isExpanded} className="flex w-full items-start justify-between gap-2 p-2 text-left">
                      <span className="min-w-0">
                        <span className="block break-words font-semibold text-slate-800 dark:text-slate-100">{String(entry.type || 'Conversation event').replaceAll('.', ' ').replaceAll('_', ' ')}</span>
                        <span className="mt-0.5 block text-[10px] text-slate-500 dark:text-slate-400">{entry.actorType || 'system'}{entry.actorId ? ` · ${entry.actorId}` : ''} · {formatActivityDate(entry.createdAt)}</span>
                      </span>
                      <span className="shrink-0 text-[10px] font-semibold text-cyan-700 dark:text-cyan-300">{isExpanded ? 'Hide' : 'Details'}</span>
                    </button>
                    {isExpanded ? <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-words border-t border-slate-200 bg-slate-50 p-2 font-mono text-[10px] leading-4 text-slate-700 dark:border-white/10 dark:bg-slate-950 dark:text-slate-200">{JSON.stringify({ fromStatus: entry.fromStatus, toStatus: entry.toStatus, payload: entry.payload || {} }, null, 2)}</pre> : null}
                  </div>
                );
              })}
              {activityEvents.length > 0 ? <button type="button" onClick={loadActivity} className="pt-1 text-[10px] font-semibold text-cyan-700 hover:underline dark:text-cyan-300">Refresh activity</button> : null}
            </div>
          ) : null}
        </section>

        <div className="rounded-xl border border-slate-200/80 bg-slate-50/70 p-2.5 text-xs dark:border-white/10 dark:bg-white/5">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">Conversation info</div>
          <dl className="mt-2 space-y-2">
            <div className="flex items-start justify-between gap-2">
              <dt className="text-slate-500 dark:text-slate-400">Channel</dt>
              <dd className="font-medium capitalize text-slate-900 dark:text-white">{conversation?.channel || '-'}</dd>
            </div>
            <div className="flex items-start justify-between gap-2">
              <dt className="text-slate-500 dark:text-slate-400">Conversation ID</dt>
              <dd className="max-w-[140px] truncate font-medium text-slate-900 dark:text-white" title={conversation?._id}>{conversation?._id || '-'}</dd>
            </div>
            <div className="flex items-start justify-between gap-2">
              <dt className="text-slate-500 dark:text-slate-400">Last update</dt>
              <dd className="font-medium text-slate-900 dark:text-white">
                {conversation?.lastMessageAt ? format(new Date(conversation.lastMessageAt), 'PP p') : '-'}
              </dd>
            </div>
          </dl>
        </div>
      </div>
    </aside>
  );
}
